import { useCallback, useEffect, useMemo, useState } from 'react'

import { fetchMaterialStock, type MaterialStockItem } from '@/features/warehouse/api/materialStock'
import {
  formatQtyByUom,
  groupStockByCategory,
  shouldCategoryDefaultOpen,
} from '@/features/warehouse/stockGroups'
import { warehouseTerms } from '@/features/warehouse/warehouseTerms'
import { CollapsibleCategoryBlock } from '@/shared/components/CollapsibleCategoryBlock'
import { EmptyState } from '@/shared/components/EmptyState'
import { SkeletonList } from '@/shared/components/Skeleton'

function formatUpdatedLabel(): string {
  return new Intl.DateTimeFormat('id-ID', { hour: '2-digit', minute: '2-digit' }).format(new Date())
}

/** GET /warehouse/material-stock — stok bahan baku gudang (§5 spec: "Stock bahan baku list"). */
export function WarehouseStockPage() {
  const [items, setItems] = useState<MaterialStockItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [search, setSearch] = useState('')
  const [refreshKey, setRefreshKey] = useState(0)
  const [updatedLabel, setUpdatedLabel] = useState('—')

  const reload = useCallback(() => {
    let cancelled = false
    setIsLoading(true)
    setLoadError(false)
    fetchMaterialStock()
      .then((res) => {
        if (!cancelled) {
          setItems(res)
          setUpdatedLabel(formatUpdatedLabel())
        }
      })
      .catch(() => {
        if (!cancelled) setLoadError(true)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => reload(), [reload, refreshKey])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return items
    return items.filter(
      (item) => item.material_name.toLowerCase().includes(q) || item.material_code.toLowerCase().includes(q),
    )
  }, [items, search])

  const groups = useMemo(() => groupStockByCategory(filtered), [filtered])

  const overviewLine =
    loadError || isLoading ? '—' : warehouseTerms.stock.summary.overview(filtered.length, groups.length)

  return (
    <section className="space-y-4">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="page-title">{warehouseTerms.stock.title}</p>
          <p className="text-body-sm text-on-surface-variant mt-1">{warehouseTerms.stock.subtitle(updatedLabel)}</p>
        </div>
        <button
          type="button"
          className="btn-outline shrink-0 min-w-[44px] px-3"
          onClick={() => setRefreshKey((k) => k + 1)}
          disabled={isLoading}
          aria-label={warehouseTerms.stock.refreshAria}
          title={warehouseTerms.stock.refreshAria}
        >
          <span className={`material-symbols-outlined ${isLoading ? 'animate-spin' : ''}`}>refresh</span>
        </button>
      </header>

      <div className="relative">
        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant text-xl pointer-events-none">
          search
        </span>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={warehouseTerms.stock.searchPlaceholder}
          className="input-field w-full pl-10"
          aria-label={warehouseTerms.stock.searchAria}
        />
      </div>

      <p className="text-body-sm text-on-surface-variant">{overviewLine}</p>

      {isLoading && <SkeletonList count={6} label={warehouseTerms.stock.loading} />}

      {loadError && (
        <div role="alert" className="alert-error flex flex-wrap items-center justify-between gap-2">
          <span className="text-body-sm">{warehouseTerms.stock.loadError}</span>
          <button type="button" className="btn-outline min-h-[36px] text-body-sm" onClick={() => setRefreshKey((k) => k + 1)}>
            {warehouseTerms.stock.retry}
          </button>
        </div>
      )}

      {!isLoading && !loadError && filtered.length === 0 && (
        <EmptyState
          icon="inventory_2"
          title={warehouseTerms.stock.empty.title}
          description={search.trim() ? warehouseTerms.stock.empty.searchDescription : warehouseTerms.stock.empty.description}
        />
      )}

      {!isLoading && !loadError && filtered.length > 0 && (
        <div className="space-y-2">
          {groups.map((g, i) => (
            <CollapsibleCategoryBlock
              key={`${g.categoryKey}-${search.trim()}`}
              categoryName={g.categoryName}
              itemCount={g.itemCount}
              itemCountLabel={warehouseTerms.stock.itemCountLabel}
              summary={formatQtyByUom(g.qtyByUom)}
              defaultOpen={shouldCategoryDefaultOpen(i, groups.length, !!search.trim())}
            >
              {g.items.map((item) => (
                <div
                  key={`${item.material_id}-${item.uom}`}
                  className="flex items-center justify-between gap-3 px-3 py-2.5 border-b border-outline-variant last:border-b-0"
                >
                  <div className="min-w-0">
                    <p className="text-body-sm font-semibold text-on-surface truncate">{item.material_name}</p>
                    <p className="text-label-md text-on-surface-variant mt-0.5 font-mono">{item.material_code}</p>
                    {item.packaging && item.pack_size ? (
                      <p className="text-label-md text-on-surface-variant mt-0.5">
                        {warehouseTerms.stock.packagingLabel(item.packaging, item.pack_size)}
                      </p>
                    ) : null}
                  </div>
                  <p className="text-body-sm font-semibold text-primary shrink-0">
                    {item.quantity_on_hand} {item.uom}
                  </p>
                </div>
              ))}
            </CollapsibleCategoryBlock>
          ))}
        </div>
      )}
    </section>
  )
}
